import { ReactNode, createContext, useContext } from 'react';
import { cn } from '../../lib/utils';

interface PillTabsContextValue {
  value: string;
  onValueChange: (value: string) => void;
}

const PillTabsContext = createContext<PillTabsContextValue | undefined>(undefined);

interface PillTabsProps {
  value: string;
  onValueChange: (value: string) => void;
  children: ReactNode;
  className?: string;
}

interface PillTabsListProps {
  children: ReactNode;
  className?: string;
}

interface PillTabsTriggerProps {
  value: string;
  children: ReactNode;
  className?: string;
}

interface PillTabsContentProps {
  value: string;
  children: ReactNode;
  className?: string;
}

export function PillTabs({ value, onValueChange, children, className }: PillTabsProps) {
  return (
    <PillTabsContext.Provider value={{ value, onValueChange }}>
      <div className={cn('w-full', className)}>
        {children}
      </div>
    </PillTabsContext.Provider>
  );
}

export function PillTabsList({ children, className }: PillTabsListProps) {
  return (
    <div className={cn(
      'flex flex-wrap gap-2',
      className
    )}>
      {children}
    </div>
  );
}

export function PillTabsTrigger({ value: triggerValue, children, className }: PillTabsTriggerProps) {
  const context = useContext(PillTabsContext);
  if (!context) throw new Error('PillTabsTrigger must be used within PillTabs');

  const { value: currentValue, onValueChange } = context;
  const isActive = currentValue === triggerValue;

  return (
    <button
      type="button"
      onClick={() => onValueChange(triggerValue)}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-all',
        'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
        isActive
          ? 'bg-blue-600 text-white shadow-sm'
          : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700',
        className
      )}
    >
      {children}
    </button>
  );
}

export function PillTabsContent({ value: contentValue, children, className }: PillTabsContentProps) {
  const context = useContext(PillTabsContext);
  if (!context) throw new Error('PillTabsContent must be used within PillTabs');

  const { value: currentValue } = context;
  const isActive = currentValue === contentValue;

  if (!isActive) return null;

  return (
    <div className={className}>
      {children}
    </div>
  );
}
